import { isNativeAppWebView } from 'utils/webview';

/**
 * Global the native app host publishes before this bundle evaluates, following the same convention
 * as `__RN_RISKIFIED_SESSION_ID__` and `__RN_MIXPANEL_NATIVE_PROPS__`.
 */
const NATIVE_AUTH_TOKEN_GLOBAL = '__RN_RESERHUB_AUTH_TOKEN__';

/**
 * Remove the token global once it has been read.
 *
 * A `var __RN_RESERHUB_AUTH_TOKEN__ = …` in the host's injected script produces a non-configurable
 * property, unlike an assignment to `window`, and `delete` on one either throws (strict mode) or
 * silently returns false. Both are handled: the delete is guarded, and the value is overwritten if
 * it survived. This runs inside boot, where an uncaught throw renders the error screen, so the
 * overwrite is guarded too — against a read-only global there is nothing further to do.
 *
 * @returns {void}
 */
function clearNativeAuthTokenGlobal() {
  try {
    delete window[NATIVE_AUTH_TOKEN_GLOBAL];
  } catch {
    // Non-configurable under strict mode; the overwrite below is the fallback.
  }

  if (window[NATIVE_AUTH_TOKEN_GLOBAL] !== undefined) {
    try {
      window[NATIVE_AUTH_TOKEN_GLOBAL] = undefined;
    } catch {
      // Read-only global: the host owns it and boot must not fail over it.
    }
  }
}

/**
 * Read — and consume — the account bearer token published by the native app host.
 *
 * A global rather than only a posted message because the account provider fetches the user on mount:
 * a message racing that first fetch would send it unauthenticated, resolve anonymous, and — for a
 * user who had been logged in — trip the session-expired warning. The global is already present when
 * the bundle evaluates, so the first fetch carries the token.
 *
 * The global is removed once read, so a live credential is not left on `window` for the rest of the
 * page. Boot is the only reader and the host republishes on every load, so consuming it costs
 * nothing — a token that changes mid-session arrives on `NATIVE_SESSION_CHANGED`, not by
 * republishing the global. This does not cover the window before boot runs, and any in-page script
 * can still observe the bearer header; it removes the standing handle, not the need to trust what
 * runs in the page.
 *
 * Only honored inside the native app WebView. Elsewhere the session cookie is the credential and no
 * host is entitled to inject one.
 *
 * @returns {string|null} The token, or null when absent, unusable, or outside the native app.
 */
export default function readNativeAuthToken() {
  if (typeof window === 'undefined' || !isNativeAppWebView()) return null;

  const token = window[NATIVE_AUTH_TOKEN_GLOBAL];
  clearNativeAuthTokenGlobal();

  return typeof token === 'string' && token.length > 0 ? token : null;
}

/**
 * Token consumed at boot whose rollout decision is not known yet.
 *
 * A progressive-rollout tenant's enablement depends on a GrowthBook feature that has not loaded when
 * boot runs, so the token is consumed from `window` (which must happen regardless) but withheld from
 * the accounts client until the real value is known.
 */
let heldToken = null;

/**
 * Hold a consumed token until the rollout decision is known.
 * @param {string|null} token - The token to hold, or null to hold nothing.
 * @returns {void}
 */
export function holdNativeAuthToken(token) {
  heldToken = token || null;
}

/**
 * Take the held token, if any, clearing the slot.
 *
 * Returns null once taken, and once for the life of the page if never taken — a visitor the rollout
 * excludes simply never receives it.
 *
 * @returns {string|null} The held token, or null.
 */
export function takeHeldNativeAuthToken() {
  const token = heldToken;
  heldToken = null;
  return token;
}

export { NATIVE_AUTH_TOKEN_GLOBAL };
